/**
 * Formats a "received" timestamp exactly, on one four-rung rule used
 * everywhere a "received at" moment is shown — the Review card, the Review
 * modal, the Activity feed, and the notification center (Kelvin, YAR-310
 * CB6, 2026-09-17; YAR-139's AC is being updated to match):
 *   - today                       → clock time        ("9:15 AM")
 *   - 1-6 calendar days back      → weekday, 3 letters ("Tue" — YAR-168 AC4;
 *                                   the long form was QA'd on YAR-311)
 *   - older, same calendar year   → month + day        ("Sep 3")
 *   - beyond the current year     → month + day + year ("Dec 28, 2025")
 *
 * Replaces the relative form ("2 hours ago", "1 day ago") every one of those
 * surfaces used before. Kelvin's reasons: relative text goes stale between
 * QA passes — a screenshot or bug report taken minutes ago reads
 * differently by the time anyone looks at it — and it can't be verified
 * deterministically, since "correct" depends on when you happen to check.
 * Every rung here is a fixed string for a fixed instant instead. One
 * timestamp language across tabs on purpose: a parent should not learn a
 * different "how recent" vocabulary depending on which screen they're on.
 *
 * "Days back" is calendar-day difference in the viewer's local zone, not a
 * 24-hour window — a message received at 23:50 yesterday is one day back
 * (renders as the weekday name), not "today" just because it's under 24h
 * old, and not "yesterday" (rung 2 has no such special case; it always
 * names the weekday).
 *
 * `now` defaults to the real clock and is only ever overridden by tests, so
 * the function stays pure and deterministic given a fixed `now`.
 *
 * The 1-6-day check runs before the calendar-year check, so an item 1-6
 * days back that crosses a year boundary (Dec 29 viewed on Jan 2) still
 * renders the weekday name — recency wins over the year rung, intentionally.
 *
 * A future `iso` (clock skew) yields a negative `daysBack`, which matches
 * neither the today nor the 1-6-day rung and so falls through to rung 3/4
 * by design — a plain date, never a crash or a misread weekday/clock-time.
 */
export function formatReceivedTimestamp(
  iso: string,
  now: Date = new Date(),
): string {
  const date = new Date(iso);
  const startOfDay = (d: Date) =>
    new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
  const daysBack = Math.round((startOfDay(now) - startOfDay(date)) / 86400000);

  if (daysBack === 0) {
    return date.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    });
  }
  if (daysBack >= 1 && daysBack <= 6) {
    return date.toLocaleDateString('en-US', { weekday: 'short' });
  }
  if (date.getFullYear() === now.getFullYear()) {
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
    });
  }
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

/**
 * Parse a date string as a local date, ignoring timezone offset.
 * Supabase returns timestamptz as "2026-03-12T00:00:00+00:00" — parsing that
 * with `new Date()` shifts it back a day in timezones west of UTC.
 * This extracts YYYY-MM-DD and builds a local Date instead.
 */
export function parseLocalDate(dateString: string): Date {
  const [y, m, d] = dateString.split('T')[0].split('-').map(Number);
  return new Date(y, m - 1, d);
}

/**
 * Parse an ISO datetime string to a Date:
 * - Date-only ("2026-03-18") → local midnight (avoids the UTC-midnight
 *   day-shift on timezones west of UTC).
 * - With an explicit offset or Z ("2026-03-18T14:00:00+00:00") → the
 *   real UTC instant via the Date constructor. Formatters then render
 *   in the user's profile / browser timezone via `toLocaleString`'s
 *   `timeZone` option.
 * - Naive datetime (no Z / offset) → interpret the wall-clock
 *   components as local. Kept for legacy rows that were persisted
 *   without an offset.
 *
 * Prior behavior stripped the offset and reused the numeric hour as
 * local — which looked fine when the DB returned the original offset,
 * but Postgres `timestamptz` normalizes to UTC, so the stored value
 * would come back as "T15:00:00+00:00" and the stripped "15" would
 * render as 3pm instead of the user-intended 9am (spec-030 bug).
 */
export function parseLocalDateTime(dateString: string): Date {
  const [datePart, timePart] = dateString.split('T');
  const [y, m, d] = datePart.split('-').map(Number);
  if (!timePart) return new Date(y, m - 1, d);
  if (/Z$|[+-]\d{2}:?\d{2}$/.test(timePart)) {
    return new Date(dateString);
  }
  // Naive datetime — interpret wall-clock components as local.
  const [h, min, s] = timePart.split(':').map(Number);
  return new Date(y, m - 1, d, h || 0, min || 0, s || 0);
}

export function formatDate(dateString: string | null): string {
  if (!dateString) return '-';
  const date = parseLocalDate(dateString);
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

export function isSameDay(a: Date, b: Date): boolean {
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
}

/**
 * Formats a task's due date as "Nov 24, 2026".
 *
 * Design spec from the April 20 QA round says the task card should
 * surface the year instead of the hour — so we always go through
 * `formatDate` here regardless of whether the stored timestamp
 * carries a specific time. The precise time is still available in
 * the edit modal; only the card surface is simplified. Null/empty
 * input yields '-'.
 */
export function formatDueDate(
  dateString: string | null,
  _timezone?: string | null,
): string {
  if (!dateString) return '-';
  return formatDate(dateString);
}

export function formatDateTime(
  dateString: string | null,
  timezone?: string | null,
): string {
  if (!dateString) return '-';
  const date = parseLocalDateTime(dateString);
  return date.toLocaleString('en-US', {
    timeZone: timezone ?? undefined,
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });
}

/**
 * True for a value that names a calendar day and nothing more
 * ("2026-06-02"), as opposed to the full instant Postgres round-trips for a
 * `timestamptz` ("2026-06-02T16:00:00+00:00").
 */
export function isDateOnlyIso(value: string): boolean {
  return !value.includes('T') || value.length <= 10;
}

/**
 * The timezone a given value may safely be projected into — `undefined`
 * (browser-local) for anything date-only.
 *
 * A date-only value carries no instant. `parseLocalDateTime` materialises it
 * as LOCAL midnight precisely so the day survives the trip. Re-projecting THAT
 * Date into another zone moves the calendar day: "2026-06-02" read from a
 * UTC+2 browser and rendered in America/Los_Angeles is Jun 1. Such a value is
 * already the day it claims to be, so it is formatted zone-free.
 *
 * Every day-level helper below routes through this, so the day a value is
 * PRINTED as and the day it is COMPARED as can never come apart.
 */
function zoneFor(iso: string, timezone?: string | null): string | undefined {
  return isDateOnlyIso(iso) ? undefined : (timezone ?? undefined);
}

/**
 * "Nov. 24" — month abbreviation + period + day. Used on review/summary
 * cards where the year is implicit.
 */
export function formatShortDate(
  dateString: string | null,
  timezone?: string | null,
): string {
  if (!dateString) return '-';
  const date = parseLocalDateTime(dateString);
  const tz = zoneFor(dateString, timezone);
  const month = date.toLocaleDateString('en-US', {
    timeZone: tz,
    month: 'short',
  });
  const day = Number(
    date.toLocaleDateString('en-US', { timeZone: tz, day: 'numeric' }),
  );
  return `${month}. ${day}`;
}

/**
 * The `YYYY-MM-DD` calendar day an ISO value falls on, as seen from
 * `timezone` (browser-local when omitted). Deliberately routed through the
 * same `parseLocalDateTime` + `toLocaleDateString` path as `formatShortDate`
 * so "are these the same day?" can never disagree with the day each endpoint
 * is actually printed as.
 */
function calendarDayKey(dateString: string, timezone?: string | null): string {
  // en-CA renders as YYYY-MM-DD, which sorts and compares as a plain string.
  return parseLocalDateTime(dateString).toLocaleDateString('en-CA', {
    timeZone: zoneFor(dateString, timezone),
  });
}

/**
 * "Aug. 17 - Dec. 28" when the two endpoints land on different calendar
 * days, "Aug. 17" when they land on the same one (or there is no end).
 *
 * YAR-391: a card that prints only `formatShortDate(start)` next to
 * `formatTimeRange(start, end)` renders a four-month tuition-liability
 * window as "Aug. 17 / 12:00 AM–11:59 PM" — indistinguishable from an
 * ordinary all-day event on Aug 17. Both halves were individually true; the
 * combination asserts something false. 16% of the events extracted in
 * production so far span more than one day (school retreats, college tours,
 * refund-policy windows), so this is not an edge case.
 *
 * Scope note: this only makes the range VISIBLE. How a multi-day event
 * should ultimately be presented — spanning every day of the Schedule, or
 * split into separate start/end items — is an open product question on
 * YAR-391 (awaiting @george, in the design backlog). This helper is
 * compatible with either outcome and is meant to be superseded by it.
 */
export function formatDateRange(
  startIso: string | null,
  endIso?: string | null,
  timezone?: string | null,
): string {
  if (!startIso) return '-';
  const start = formatShortDate(startIso, timezone);
  if (!endIso) return start;
  if (!spansMultipleDays(startIso, endIso, timezone)) return start;
  return `${start}–${formatShortDate(endIso, timezone)}`;
}

/**
 * Whether the two endpoints land on different calendar days in the display
 * zone — i.e. whether a card must surface a range at all.
 *
 * Exported so a surface can DECIDE to render its date row without having to
 * sniff the formatted string for a separator. `formatDateRange` is defined in
 * terms of this same predicate, so the gate and the text it gates cannot
 * disagree: a card never shows a "range" row holding a single date.
 */
export function spansMultipleDays(
  startIso: string | null,
  endIso?: string | null,
  timezone?: string | null,
): boolean {
  if (!startIso || !endIso) return false;
  return (
    calendarDayKey(startIso, timezone) !== calendarDayKey(endIso, timezone)
  );
}

/**
 * "Mon, Aug 17–Mon, Dec 28" — the weekday-bearing form the detail modal's
 * date pill uses, range-aware on the same terms as `formatDateRange`.
 *
 * Replaces a local `formatDetailDate` built on `parseLocalDate`, which read
 * the raw ISO date half and ignored the zone entirely. On the row YAR-391 was
 * filed against that printed the end as Dec 29 — a day the email never
 * mentions, and one the Review card (which does honour the zone) contradicts
 * on the very same item.
 */
export function formatLongDateRange(
  startIso: string | null,
  endIso?: string | null,
  timezone?: string | null,
): string {
  if (!startIso) return '-';
  const start = formatLongDate(startIso, timezone);
  if (!endIso) return start;
  if (!spansMultipleDays(startIso, endIso, timezone)) return start;
  return `${start}–${formatLongDate(endIso, timezone)}`;
}

/** "Tue, Jun 2" — one endpoint of {@link formatLongDateRange}. */
function formatLongDate(iso: string, timezone?: string | null): string {
  return parseLocalDateTime(iso).toLocaleDateString('en-US', {
    timeZone: zoneFor(iso, timezone),
    weekday: 'short',
    month: 'short',
    day: 'numeric',
  });
}

/**
 * Whether the span covers its days end to end — midnight through 23:59 in the
 * display zone.
 *
 * The tuition-liability window that prompted YAR-391 is stored as
 * 00:00:00 → 23:59:59, so the clock row rendered "12:00 AM–11:59 PM": true,
 * and useless. "All day" says the same thing in a way a parent can act on.
 *
 * Deliberately keyed on the times BEING a full day, never on a time being
 * absent — those are opposite signals. A date-only extraction is stored at
 * local NOON as a day-boundary guard, and calling that "all day" would assert
 * a duration the email never stated. Absence of a time is handled by
 * suppressing the clock entirely (`is_time_unknown`), not by relabelling it.
 */
export function isAllDaySpan(
  startIso: string,
  endIso?: string | null,
  timezone?: string | null,
): boolean {
  if (!endIso) return false;
  const clock = (iso: string) =>
    parseLocalDateTime(iso).toLocaleTimeString('en-GB', {
      timeZone: zoneFor(iso, timezone),
      hour: '2-digit',
      minute: '2-digit',
    });
  return clock(startIso) === '00:00' && clock(endIso) === '23:59';
}

/** "4:00 PM" — 12-hour clock in forced en-US locale. */
export function formatTime(
  dateString: string,
  timezone?: string | null,
): string {
  const date = parseLocalDateTime(dateString);
  return date.toLocaleTimeString('en-US', {
    timeZone: timezone ?? undefined,
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });
}

/**
 * The trailing " AM" / " PM" of a `formatTime` result. ICU separates it with
 * a plain space in Node and a NARROW NO-BREAK SPACE (U+202F) in Chrome 110+
 * and Safari 16.4+; `\s` covers both, and the separator is never rebuilt —
 * the end time keeps whichever one the browser produced.
 */
const MERIDIEM_RE = /\s?(AM|PM)$/i;

/**
 * "4:00–4:30 PM" when both ends share the meridiem, "11:00 AM–1:00 PM" when
 * the range crosses noon, just the start when there is no end.
 *
 * Saying AM/PM once per range is the YAR-420 wording ("1:00–3:00 PM"),
 * confirmed by Kelvin at the Sep 21 stand-up over Figma's longer form. Every
 * time range a parent sees goes through here (Review cards, event cards,
 * Event Details), so the rule lives in one place.
 *
 * Times ONLY, even when the end falls on a later day — the calendar half of
 * a cross-day range belongs to `formatDateRange`, which surfaces have their
 * own slot for. Adding a date here would print it twice on any card that
 * already renders both (YAR-391).
 */
export function formatTimeRange(
  startIso: string,
  endIso?: string | null,
  timezone?: string | null,
): string {
  const start = formatTime(startIso, timezone);
  if (!endIso) return start;
  const end = formatTime(endIso, timezone);
  const startMeridiem = start.match(MERIDIEM_RE);
  const endMeridiem = end.match(MERIDIEM_RE);
  const shared =
    startMeridiem &&
    endMeridiem &&
    startMeridiem[1].toUpperCase() === endMeridiem[1].toUpperCase();
  const startClock = shared ? start.slice(0, startMeridiem.index) : start;
  return `${startClock}–${end}`;
}

const COMPACT_MONTHS = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec',
];

/**
 * "Aug 10–14" · "Aug 28–Sep 2" — the History span row's meta line
 * (YAR-176 AC 12).
 *
 * Deliberately not built on {@link formatShortDate}: that renders "Aug. 17"
 * with a trailing period, and the approved copy is three-letter months with
 * none. It also collapses a repeated month, which no other formatter does —
 * "Aug 10–Aug 14" reads as two separate dates rather than one span.
 *
 * The year is carried only when the range is not in the current one, and only
 * once — unless the span crosses new year, where both endpoints need it to
 * stay unambiguous.
 */
export function formatCompactDateRange(
  startIso: string | null,
  endIso?: string | null,
  timezone?: string | null,
  /** YYYY-MM-DD "today"; defaults to the clock. Pass it to keep this pure. */
  todayKey?: string,
): string {
  if (!startIso) return '-';

  const currentYear = todayKey
    ? Number(todayKey.slice(0, 4))
    : new Date().getFullYear();

  const parts = (iso: string) => {
    const key = calendarDayKey(iso, timezone);
    const [y, m, d] = key.split('-').map(Number);
    return { y, m, d };
  };

  const s = parts(startIso);
  const withYear = (p: { y: number; m: number; d: number }) =>
    `${COMPACT_MONTHS[p.m - 1]} ${p.d}${p.y === currentYear ? '' : `, ${p.y}`}`;

  if (!endIso) return withYear(s);
  const e = parts(endIso);

  // Same day: there is no range to draw.
  if (s.y === e.y && s.m === e.m && s.d === e.d) return withYear(s);

  // Crossing new year — each endpoint has to name its own.
  if (s.y !== e.y) {
    return `${COMPACT_MONTHS[s.m - 1]} ${s.d}, ${s.y}–${COMPACT_MONTHS[e.m - 1]} ${e.d}, ${e.y}`;
  }

  const yearSuffix = s.y === currentYear ? '' : `, ${s.y}`;
  const body =
    s.m === e.m
      ? `${COMPACT_MONTHS[s.m - 1]} ${s.d}–${e.d}`
      : `${COMPACT_MONTHS[s.m - 1]} ${s.d}–${COMPACT_MONTHS[e.m - 1]} ${e.d}`;
  return `${body}${yearSuffix}`;
}
